import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canChooseOutput } from '../audio/context';
import { deviceName, systemDefault } from '../audio/devices';
import { closeMicrophone, openMicrophone } from '../audio/mic';
import { saveSpeaker, useRehearsalSpeaker } from '../audio/speakers';
import ui from '../styles/ui.module.css';
import styles from './SpeakerPicker.module.css';

/**
 * Rehearsal mode's speakers, in the header's audio menu: the computer's outputs, the choice kept
 * in this browser (audio/speakers.ts). The rehearsal page plays through them; choosing here never
 * changes what practice plays through. The browser names the devices only after microphone
 * permission; a button asks for it once (nothing is recorded, the microphone closes at once).
 */
export function SpeakerPicker() {
  const { t } = useTranslation();
  const { choice, outputs, real, deviceId, missing, refresh } = useRehearsalSpeaker();
  const [askError, setAskError] = useState(false);

  if (!canChooseOutput()) {
    return (
      <p className={styles.note} data-testid="speaker-picker">
        {t('speakers.notSupported')}
      </p>
    );
  }

  const named = real.some((d) => d.label);
  const defaultName = systemDefault(outputs)?.label;
  const showNames = async () => {
    setAskError(false);
    try {
      closeMicrophone(await openMicrophone());
      refresh();
    } catch {
      setAskError(true);
    }
  };

  return (
    <div className={styles.picker} data-testid="speaker-picker" data-output={deviceId || 'default'}>
      <label className={styles.field}>
        <span className={ui.visuallyHidden}>{t('speakers.label')}</span>
        <select
          className={`${ui.select} ${styles.select}`}
          value={deviceId}
          onChange={(e) => {
            const device = real.find((d) => d.deviceId === e.target.value);
            saveSpeaker(device ? { deviceId: device.deviceId, label: device.label } : null);
          }}
        >
          <option value="">{defaultName ? t('speakers.systemDefaultNamed', { name: deviceName(defaultName) }) : t('speakers.systemDefault')}</option>
          {real.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label ? deviceName(d.label) : t('speakers.unnamed', { n: i + 1 })}
            </option>
          ))}
        </select>
      </label>
      {!named && real.length > 0 && (
        <button type="button" className={ui.linkButton} onClick={() => void showNames()} title={t('speakers.namesHint')}>
          {t('speakers.showNames')}
        </button>
      )}
      {missing && <p className={styles.note}>{t('speakers.missing', { name: deviceName(choice?.label ?? '') })}</p>}
      {askError && <p className={styles.note}>{t('speakers.namesDenied')}</p>}
      <p className={styles.note}>{t('speakers.remembered')}</p>
    </div>
  );
}
